import { useCallback, useEffect, useState } from 'react';
import { api, type User } from '../api.ts';
import Icon from '../components/Icon.tsx';

interface Action { key: string; label: string }
interface Screen { key: string; label: string; actions: Action[] }
interface Section { key: string; label: string; screens: Screen[] }
interface Role {
  role_name: string; description: string | null; active: boolean;
  is_system: boolean; permissions: string[]; active_users: string;
}

/**
 * Roles and permissions.
 *
 * Each screen shows only the actions it actually supports -- the production
 * route is read-only, board has a "mark ordered" that is not an ordinary
 * update. A uniform create/read/update/delete grid would offer ticks that do
 * nothing, and a tick that does nothing is worse than a missing one because
 * somebody believes they granted it.
 */
export default function Roles({ user }: { user: User }) {
  const [catalogue, setCatalogue] = useState<Section[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', description: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const canManage = user.permissions?.includes('settings_roles.manage') ?? false;

  const load = useCallback(async () => {
    setError('');
    try {
      const [c, r] = await Promise.all([
        api.get<{ catalogue: Section[] }>('/api/permissions'),
        api.get<{ roles: Role[] }>('/api/roles'),
      ]);
      setCatalogue(c.catalogue);
      setRoles(r.roles);
      setSelected((prev) => prev ?? r.roles[0]?.role_name ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load roles');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const role = roles.find((r) => r.role_name === selected);

  // Reset the draft whenever a different role is opened.
  useEffect(() => {
    if (role) setDraft(new Set(role.permissions));
  }, [role?.role_name, role?.permissions.length]);

  const dirty = role
    ? role.permissions.length !== draft.size
      || role.permissions.some((p) => !draft.has(p))
    : false;

  function toggle(key: string) {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function sectionKeys(s: Section): string[] {
    return s.screens.flatMap((sc) => sc.actions.map((a) => `${sc.key}.${a.key}`));
  }

  /** The whole-section switch: the fast way to revoke an area. */
  function toggleSection(s: Section, on: boolean) {
    const keys = sectionKeys(s);
    setDraft((prev) => {
      const next = new Set(prev);
      for (const k of keys) { if (on) next.add(k); else next.delete(k); }
      return next;
    });
  }

  async function save() {
    if (!role) return;
    setBusy(true); setError('');
    try {
      await api.patch(`/api/roles/${encodeURIComponent(role.role_name)}`,
                      { permissions: [...draft] });
      setNotice(`${role.role_name} saved · ${draft.size} permissions`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the role');
    } finally { setBusy(false); }
  }

  async function create() {
    setBusy(true); setError('');
    try {
      await api.post('/api/roles', {
        name: newRole.name.trim(),
        ...(newRole.description.trim() ? { description: newRole.description.trim() } : {}),
        permissions: [],
      });
      setNotice(`${newRole.name.trim()} created · tick what it may reach`);
      setSelected(newRole.name.trim());
      setNewRole({ name: '', description: '' });
      setAdding(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the role');
    } finally { setBusy(false); }
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>Roles and permissions</h2>
        <div className="spacer" />
        {canManage && (
          <button className="btn primary" onClick={() => { setNotice(''); setAdding((v) => !v); }}>
            {adding ? 'Cancel' : 'New role'}
          </button>
        )}
      </div>

      {error && <div className="error" style={{ margin: 16 }}>{error}</div>}
      {notice && <div style={{ margin: 16 }} className="tag ready notice">{notice}</div>}

      {adding && (
        <div className="add-row">
          <div className="field">
            <label className="req">Role name</label>
            <input type="text" placeholder="Printing" value={newRole.name}
                   onChange={(e) => setNewRole((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="field">
            <label>What it is for</label>
            <input type="text" value={newRole.description}
                   onChange={(e) => setNewRole((p) => ({ ...p, description: e.target.value }))} />
          </div>
          <button className="btn primary" disabled={busy || newRole.name.trim().length < 2}
                  onClick={create}>
            Create role
          </button>
        </div>
      )}

      <div className="stagebar">
        {roles.map((r) => (
          <button key={r.role_name}
                  className={`chip ${selected === r.role_name ? 'on' : ''}`}
                  onClick={() => setSelected(r.role_name)}>
            {r.role_name}
            <span className="chip-n">{r.active_users}</span>
          </button>
        ))}
      </div>

      {role && (
        <>
          <div className="stagebar" style={{ borderTop: 0 }}>
            <span className="hint">
              {role.description ?? 'No description'}
              {role.is_system && ' · built in — its access cannot be reduced, because '
                + 'it is what lets you repair a role that has been set up wrongly'}
            </span>
            <div className="spacer" />
            {canManage && !role.is_system && (
              <button className="btn primary" disabled={busy || !dirty} onClick={save}>
                {busy ? 'Saving…' : dirty ? `Save (${draft.size})` : 'Saved'}
              </button>
            )}
          </div>

          <div style={{ padding: '0 16px 16px' }}>
            {catalogue.map((s) => {
              const keys = sectionKeys(s);
              const on = keys.filter((k) => draft.has(k)).length;
              const isCollapsed = collapsed.has(s.key);
              return (
                <div key={s.key} className="perm-section">
                  <div className="perm-head">
                    <button className="icon-btn" style={{ width: 26, height: 26 }}
                            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                            onClick={() => setCollapsed((prev) => {
                              const n = new Set(prev);
                              if (n.has(s.key)) n.delete(s.key); else n.add(s.key);
                              return n;
                            })}>
                      <Icon name="chevron" size={13}
                            className={isCollapsed ? '' : 'nav-chevron open'} />
                    </button>
                    <strong>{s.label}</strong>
                    <span className="dim">{on} of {keys.length}</span>
                    <div className="spacer" />
                    {canManage && !role.is_system && (
                      <button className="btn"
                              onClick={() => toggleSection(s, on < keys.length)}>
                        {on === keys.length ? 'Turn all off'
                          : on === 0 ? 'Turn all on' : 'Turn all on'}
                      </button>
                    )}
                  </div>

                  {!isCollapsed && (
                    <div className="tbl">
                      <table>
                        <tbody>
                          {s.screens.map((sc) => (
                            <tr key={sc.key}>
                              <td style={{ width: 190 }}>{sc.label}</td>
                              <td>
                                <div className="perm-actions">
                                  {sc.actions.map((a) => {
                                    const key = `${sc.key}.${a.key}`;
                                    return (
                                      <label key={key} className="perm-check">
                                        <input type="checkbox"
                                               checked={draft.has(key)}
                                               disabled={!canManage || role.is_system}
                                               onChange={() => toggle(key)} />
                                        {a.label}
                                      </label>
                                    );
                                  })}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
